<template>
  <div>
    <AnimatedProcessIndicator
      v-if="isProcessing"
      :text="$t('APP.RELEASES.BUNDLE.IS_PROCESSING')"
    />

    <BundleSignUploader
      v-else-if="isWaitingForSign"
      :upload-path="binFileUploadPath"
      :aab-link="bundle.bundleLink"
      @upload-done="updateLocalBundleData"
    />

    <ErrorsList v-if="errors.length" :errors="errors" />
  </div>
</template>

<script>
import { Bundle } from '@/api/api';
import { apiPath } from '@/api/constants';
import AnimatedProcessIndicator from '@@/common/AnimatedProcessIndicator';
import BundleSignUploader from '@@/app/common/BundleSignUploader';
import ErrorsList from '@@/common/ErrorsList';

import packageName from '@/mixins/packageName';

import { BUNDLE_STATES } from '@@/app/common/constants';
import { mapActions } from 'vuex';
import {
  RELEASES,
  UNCOMMITTED_RELEASE,
  UPDATE_UNCOMMITTED_RELEASE,
} from '@/store/types';

const BUNDLE_FETCH_INTERVAL_TIME = 20 * 1000;

export default {
  name: 'ReleaseBundle',
  components: {
    AnimatedProcessIndicator,
    BundleSignUploader,
    ErrorsList,
  },
  mixins: [packageName],
  props: {
    bundle: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      bundleFetchInterval: null,
    };
  },
  computed: {
    bundleState() {
      return this.bundle.state.toUpperCase();
    },
    isProcessing() {
      return (
        this.bundleState === BUNDLE_STATES.PROCESSING ||
        this.bundleState === BUNDLE_STATES.SIGNED
      );
    },
    isWaitingForSign() {
      return this.bundleState === BUNDLE_STATES.UPLOADED;
    },
    errors() {
      return this.bundle.errors || [];
    },
    binFileUploadPath() {
      return `${apiPath}/apps/${this.packageName}/bundle/${this.bundle?.id}/sign/`;
    },
  },
  watch: {
    isProcessing: {
      immediate: true,
      handler() {
        if (this.isProcessing && !this.bundleFetchInterval) {
          this.bundleFetchInterval = setInterval(() => {
            this.fetchBundle();
          }, BUNDLE_FETCH_INTERVAL_TIME);
        }
        if (!this.isProcessing) {
          clearInterval(this.bundleFetchInterval);
        }
      },
    },
  },
  beforeDestroy() {
    clearInterval(this.bundleFetchInterval);
  },
  methods: {
    ...mapActions({
      fetchReleasesList: RELEASES,
      fetchUncommittedRelease: UNCOMMITTED_RELEASE,
      updateUncommittedRelease: UPDATE_UNCOMMITTED_RELEASE,
    }),
    fetchBundle() {
      Bundle.getBundle({
        packageName: this.packageName,
        id: this.bundle.id,
      })
        .then(bundle => {
          if (bundle.state === BUNDLE_STATES.DONE) {
            clearInterval(this.bundleFetchInterval);
            this.refreshReleases();
          }

          this.updateUncommittedRelease({ bundle });
        })
        .catch(({ message }) => {
          this.$message.error(message);
        });
    },
    refreshReleases() {
      this.fetchReleasesList(this.packageName);
      this.fetchUncommittedRelease(this.packageName);
    },
    updateLocalBundleData(bundle) {
      this.updateUncommittedRelease({ bundle });
    },
  },
};
</script>
